import { useState, useMemo, useCallback, useEffect } from 'react';
import Fuse from 'fuse.js/dist/fuse.basic.esm';

import dataDictionary from '../../assets/data-dictionary/dataDictionary.json';
import DraggableStaticList from './DraggableStaticList';
import DraggableAttribute from './DraggableAttribute';
import { TYPES } from '../../reducers/builderReducer';
import {
  isHiddenAttribute,
  isNewCategory,
  parseAllowedZoneTypes,
  traverse,
} from '../../helpers/dataDictionary';
import SearchBox from '../inputs/SearchBox';
import { Tooltip } from 'react-tooltip';

const { VARIABLE_ATTRIBUTE } = TYPES;

let attributeSearchList = [];
let categorySearchList = [];

for (const [key, value, path] of traverse(dataDictionary)) {
  if (key === 'attributes') {
    value.forEach(({ attributeName, attributeDescription, id, attrs }) => {
      if (isHiddenAttribute(attrs?.key)) return;
      const categories = id.split('___');

      attributeSearchList.push({
        attributeName: attributeName.toLowerCase(),
        attributeDescription: attributeDescription.toLowerCase(),
        id,
        category: categories[0],
        subCategory0: categories[1] || undefined,
        subCategory1: categories[2] || undefined,
        subCategory2: categories[3] || undefined,
      });
    });
  } else if (typeof value === 'object' && value !== null) {
    categorySearchList.push({
      name: key.toLowerCase(),
      path: path.join('___'),
    });
  }
}

const attributeFuse = new Fuse(attributeSearchList, {
  minMatchCharLength: 3,
  threshold: 0.2,
  includeScore: true,
  keys: [
    'attributeName',
    'attributeDescription',
    'category',
    'subCategory0',
    'subCategory1',
    'subCategory2',
  ],
});

const categoryFuse = new Fuse(categorySearchList, {
  minMatchCharLength: 2,
  threshold: 0.2,
  keys: ['name'],
});

function hasNestedChildrenInResults(children: any, searchResults: any[]) {
  let hasNestedChildAttributes = false;

  for (const [key, value] of traverse(children)) {
    if (
      key === 'attributes' &&
      Array.isArray(value) &&
      value.length &&
      value.some((attribute) =>
        searchResults.find(({ item }) => attribute.id === item.id)
      )
    ) {
      hasNestedChildAttributes = true;
      break;
    }
  }

  return hasNestedChildAttributes;
}

function shouldShowCategory(
  categoryPath: string[],
  children: any,
  searchResults: any[],
  searchMode: string
) {
  if (searchMode === 'variables') {
    return hasNestedChildrenInResults(children, searchResults);
  }

  const pathStr = categoryPath.join('___');

  return searchResults.some(({ item }) => {
    const matchPath = item.path;
    if (!matchPath) return false;

    return (
      pathStr === matchPath ||
      matchPath.startsWith(pathStr + '___') ||
      pathStr.startsWith(matchPath + '___')
    );
  });
}

function isInitialOpen(
  categoryPath: string[],
  searchResults: any[],
  searchMode: string,
  searchStr: string
) {
  if (searchStr === '') return false;

  if (searchMode === 'variables') {
    return true;
  }

  const pathStr = categoryPath.join('___');

  return searchResults.some(({ item }) => {
    const matchPath = item.path;
    if (!matchPath) return false;

    return pathStr === matchPath || matchPath.startsWith(pathStr + '___');
  });
}

export default function DataDictionary() {
  const [searchStr, setSearchStr] = useState('');
  const [searchMode, setSearchMode] = useState('variables');

  const searchResults = useMemo(() => {
    const fuse = searchMode === 'variables' ? attributeFuse : categoryFuse;
    return fuse.search(searchStr).slice(0, 10);
  }, [searchStr, searchMode]);

  const renderChildren = useCallback(
    (children: any, currentPath: string[] = []) => {
      let nodes: JSX.Element[] = [];

      Object.entries(children).forEach(([key, subChildren]) => {
        if (key === 'attributes') {
          const attributes = subChildren as any[];
          if (attributes.length) {
            const listItems: JSX.Element[] = [];

            const filteredChildren =
              searchStr !== '' && searchMode === 'variables'
                ? attributes.filter((attribute) =>
                    searchResults.find(({ item }) => attribute.id === item.id)
                  )
                : attributes;
            filteredChildren.forEach((attribute) => {
              // Skip variables marked as enhancements-only
              if (attribute.attrs.allowedZoneTypes === 'enhancements-only') {
                return;
              }
              // Skip variables withheld while they're reworked
              if (isHiddenAttribute(attribute.attrs.key)) {
                return;
              }

              listItems.push(
                <div key={attribute.id} className="flex gap-2">
                  <DraggableAttribute
                    name={attribute.attributeName}
                    id={attribute.id}
                    type={VARIABLE_ATTRIBUTE}
                    allowedZoneTypes={parseAllowedZoneTypes(
                      attribute.attrs.allowedZoneTypes
                    )}
                    tooltipId="data-dictionary-tooltip"
                    tooltipContent={attribute.attributeDescription}
                  />
                </div>
              );
            });

            if (listItems.length > 0) {
              nodes.push(<div>{listItems.map((listItem) => listItem)}</div>);
            }
          }
          return;
        }

        // categoryDescription is metadata on the category node, not a
        // subcategory — skip it so it isn't rendered as a nested folder.
        if (key === 'categoryDescription') {
          return;
        }

        const nextPath = [...currentPath, key];
        const description = (subChildren as any)?.categoryDescription;

        if (searchStr === '') {
          nodes.push(
            <DraggableStaticList
              key={key}
              label={key}
              description={description}
              tooltipId="data-dictionary-tooltip"
              items={renderChildren(subChildren, nextPath)}
              initialOpen={false}
            />
          );
          return;
        }

        if (
          shouldShowCategory(nextPath, subChildren, searchResults, searchMode)
        ) {
          nodes.push(
            <DraggableStaticList
              key={key}
              label={key}
              description={description}
              tooltipId="data-dictionary-tooltip"
              items={renderChildren(subChildren, nextPath)}
              initialOpen={isInitialOpen(
                nextPath,
                searchResults,
                searchMode,
                searchStr
              )}
            />
          );
        }
      });

      return nodes;
    },
    [searchResults, searchStr, searchMode]
  );

  const filteredDataDictionary = useMemo(
    () =>
      Object.entries(dataDictionary)
        .filter(([key]) => key)
        .map(([key, children]) => {
          const currentPath = [key];
          return searchStr === '' ||
            shouldShowCategory(
              currentPath,
              children,
              searchResults,
              searchMode
            ) ? (
            <div key={key}>
              <DraggableStaticList
                label={key}
                isNew={isNewCategory(key)}
                description={(children as any)?.categoryDescription}
                tooltipId="data-dictionary-tooltip"
                items={renderChildren(children, currentPath)}
                initialOpen={isInitialOpen(
                  currentPath,
                  searchResults,
                  searchMode,
                  searchStr
                )}
              />
            </div>
          ) : null;
        }),
    [renderChildren, searchResults, searchStr, searchMode]
  );

  return (
    <div data-tour="data-dictionary" className="flex flex-col w-full">
      <SearchBox
        setParentSearchStr={setSearchStr}
        searchMode={searchMode}
        setSearchMode={setSearchMode}
      />
      <ul className="w-full">{filteredDataDictionary}</ul>
      <Tooltip
        id="data-dictionary-tooltip"
        place="right"
        style={{ maxWidth: 300 }}
        delayShow={1000}
      />
    </div>
  );
}
